import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
  Compass,
  Facebook,
  FileBarChart,
  FlaskConical,
  Instagram,
  Rocket,
  Search,
  Target,
  TrendingUp,
  UserCheck,
  Youtube,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { CTAButton } from "../components/CTAButton";
import { CTABanner } from "../components/CTABanner";
import { BrandsMarquee } from "../components/BrandsMarquee";

const heroLines = [
  <>We turn ad spend</>,
  <>into <span className="text-brand-600">predictable</span></>,
  <>growth<span className="text-brand-600">.</span></>,
];

const services = [
  {
    icons: [Facebook, Instagram],
    title: "Meta Ads",
    desc: "Reach the right audience on Facebook and Instagram with creative-led campaigns built to generate leads and sales.",
    testid: "meta-ads-card",
    ctaTestid: "meta-ads-cta-button",
  },
  {
    icons: [Search, Youtube],
    title: "Google Ads",
    desc: "Capture people who are already searching for what you sell, through Search, Display, YouTube and Performance Max.",
    testid: "google-ads-card",
    ctaTestid: "google-ads-cta-button",
  },
];

const whyUs = [
  {
    icon: Target,
    title: "Performance first",
    desc: "We measure success by leads, sales and ROI, not vanity metrics.",
  },
  {
    icon: FileBarChart,
    title: "Transparent reporting",
    desc: "You always know where your money is going.",
  },
  {
    icon: FlaskConical,
    title: "Data-driven optimisation",
    desc: "Constant testing of creatives, audiences and landing pages.",
  },
  {
    icon: UserCheck,
    title: "Dedicated team",
    desc: "A focused point of contact for your account.",
  },
];

const steps = [
  { icon: Compass, num: "01", title: "Discover", desc: "We study your business, goals and audience." },
  { icon: Rocket, num: "02", title: "Launch", desc: "We build and launch high-intent campaigns." },
  { icon: TrendingUp, num: "03", title: "Scale", desc: "We optimise weekly and scale what works." },
];

const bars = [38, 52, 46, 64, 58, 78, 70, 92];

const DashboardVisual = ({ yCard, yChip1, yChip2 }) => (
  <motion.div style={{ y: yCard }} className="relative" data-testid="hero-dashboard">
    <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-2xl shadow-brand-900/10">
      <div className="flex items-center justify-between">
        <p className="font-display text-sm font-semibold text-slate-900">Growth dashboard</p>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-[11px] font-semibold text-brand-700">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-600 animate-pulse" />
          LIVE
        </span>
      </div>
      <div className="mt-8 flex h-44 items-end gap-2.5 sm:gap-3">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            animate={{ height: `${h}%` }}
            transition={{ duration: 0.9, delay: 0.6 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className={`flex-1 rounded-t-lg ${i === bars.length - 1 ? "bg-brand-600" : "bg-brand-100"}`}
          />
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        <span className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">Meta Ads</span>
        <span className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">Google Ads</span>
        <span className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">ROI-first</span>
      </div>
    </div>

    <motion.div
      style={{ y: yChip1 }}
      className="absolute -top-6 -right-3 sm:-right-8 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-xl shadow-brand-900/10"
      data-testid="hero-chip-leads"
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Leads</p>
      <p className="font-display text-lg font-bold text-brand-600">Trending up</p>
    </motion.div>
    <motion.div
      style={{ y: yChip2 }}
      className="absolute -bottom-6 -left-3 sm:-left-8 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-xl shadow-brand-900/10"
      data-testid="hero-chip-cost"
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Cost per result</p>
      <p className="font-display text-lg font-bold text-slate-900">Optimised weekly</p>
    </motion.div>
  </motion.div>
);

const Hero = () => {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const yCard = useTransform(scrollY, [0, 700], [0, reduce ? 0 : -50]);
  const yChip1 = useTransform(scrollY, [0, 700], [0, reduce ? 0 : -110]);
  const yChip2 = useTransform(scrollY, [0, 700], [0, reduce ? 0 : -80]);

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 dot-grid" aria-hidden="true" />
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-brand-100 opacity-70 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 md:pt-44 md:pb-28 grid lg:grid-cols-[1.05fr_1fr] gap-16 items-center">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-700"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-600" />
            Performance Marketing Agency
          </motion.span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.06]">
            {heroLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={reduce ? false : { y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-slate-600"
            data-testid="hero-subheadline"
          >
            Chatbudds is a performance marketing agency that runs Meta Ads and Google Ads for brands that want
            leads, sales and a clear return on every rupee.
          </motion.p>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <CTAButton testid="hero-cta-button">Get a Free Strategy Call</CTAButton>
            <span className="text-sm font-medium text-slate-500">Meta Ads · Google Ads · ROI-first</span>
          </motion.div>
        </div>
        <DashboardVisual yCard={yCard} yChip1={yChip1} yChip2={yChip2} />
      </div>
    </section>
  );
};

const SectionHeading = ({ eyebrow, title, sub }) => (
  <div className="max-w-2xl">
    <Reveal>
      <span className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
        {eyebrow}
      </span>
    </Reveal>
    <Reveal delay={0.1}>
      <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{title}</h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.2}>
        <p className="mt-4 text-base md:text-lg text-slate-600">{sub}</p>
      </Reveal>
    )}
  </div>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Chatbudds — Meta & Google Ads Performance Marketing Agency"
        description="Chatbudds runs Meta Ads and Google Ads that turn ad spend into predictable growth — leads, sales and a clear return on every rupee. Book a free strategy call."
      />
      <Hero />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="what-we-do-section">
        <SectionHeading
          eyebrow="What we do"
          title="Two platforms. One goal: your growth."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-2 md:gap-8">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12}>
              <div
                data-testid={s.testid}
                className="group relative h-full overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 transition-all duration-300 hover:border-brand-300 hover:shadow-xl hover:shadow-brand-900/5"
              >
                <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-brand-50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                <div className="relative">
                  <div className="flex gap-3">
                    {s.icons.map((Icon, j) => (
                      <span key={j} className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                        <Icon className="h-5 w-5" />
                      </span>
                    ))}
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-slate-600">{s.desc}</p>
                  <div className="mt-8">
                    <CTAButton variant="outline" testid={s.ctaTestid}>Get Started</CTAButton>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-slate-50/70" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHeading
            eyebrow="Why choose us"
            title="Built for businesses that count every rupee."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyUs.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1}>
                <div
                  data-testid={`why-us-card-${i + 1}`}
                  className="h-full rounded-3xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-900/5"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-600 text-white">
                    <w.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Brands we have worked with"
            title="Trusted by teams that expect results."
          />
        </div>
        <div className="mt-12">
          <BrandsMarquee />
        </div>
        <p className="mt-6 text-center text-sm font-medium text-slate-500">…and many more.</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24" data-testid="how-it-works-section">
        <SectionHeading
          eyebrow="How it works"
          title="From first call to compounding growth."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-3 md:gap-8">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12}>
              <div
                data-testid={`step-card-${i + 1}`}
                className="relative h-full rounded-3xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-900/5"
              >
                <span className="font-display text-5xl font-bold text-brand-100">{s.num}</span>
                <span className="absolute right-8 top-8 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <s.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-display text-xl font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABanner
        title="Ready to grow with performance marketing?"
        buttonLabel="Book Your Free Strategy Call"
        testid="home-final-cta-button"
      />
    </>
  );
}
